<template>
    <div class="team-list-wrapper mt-3">
        <b-table class="w-100" thead-class="table-header" hover :items="userItems" :fields="fields" responsive="sm">
            <template #cell(name)="data">
                <div class="d-flex">
                    <div class="pr-3">
                        <div class="avatar-frame" style="position: relative">
                            <div class="avatar-background" style="width: 40px; height: 40px">
                                <span class="avatar-initials">{{ data.item.initials }}</span>
                            </div>
                        </div>
                    </div>
                    <div>
                        <div class="mb-1">
                            {{ data.item.user.Name }}
                            <span
                                v-if="data.item.accessMode == AccessMode.RESTRICTED"
                                class="ml-2 px-2 badge2 badge-secondary"
                                >Collaborator
                            </span>
                        </div>
                        <div class="d-flex user-subtitle">
                            <div>{{ data.item.user.Username }}</div>
                            <div class="px-2">|</div>
                            <div>{{ data.item.user.Email }}</div>
                        </div>
                    </div>
                </div>
            </template>

            <template #cell(actions)="data">
                <div v-if="!user?.ImpersonatedFromChildUser" class="row-actions">
                    <c-dropdown
                        label=""
                        tooltip="Options"
                        :items="getCDropdownActionsForItem(data.item)"
                        variant="empty"
                        icon="fa-ellipsis"
                        class="options-button"
                    >
                        <template #dropdown-menu-item="{ label, disabled }">
                            <div
                                :class="disabled ? 'disabled-element' : undefined"
                                :disabled="disabled"
                                class="d-flex align-items-center w-100"
                            >
                                <span class="w-100">{{ label }}</span>
                            </div>
                        </template>
                    </c-dropdown>
                </div>
            </template>
        </b-table>
    </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import type { CDropdownItem } from '@/components/shared/CDropdown';
import { DateFormat, formatDate } from '@/utils/dates';
import store from '@/store';
import { AccessMode, EntityType, TeamMember } from '@/api/typings/team';
import CDropdown from '@/components/shared/CDropdown';

const user = computed(() => store.state.user);

const props = defineProps<{
    members: TeamMember[];
}>();

const emit = defineEmits<{
    (event: 'edit-member', value: TeamMember): void;
    (event: 'manage-member-entity-access', value: { member: TeamMember; entityType: EntityType }): void;
    (event: 'delete-member', value: TeamMember): void;
}>();

const fields = [
    {
        key: 'name',
        label: 'Name',
        sortable: true
    },
    {
        key: 'date',
        label: 'Creation date',
        sortable: true
    },
    {
        key: 'actions',
        label: '',
        sortable: false
    }
];

const getCDropdownActionsForItem = (item: TeamMember): CDropdownItem[] => {
    return user.value?.ImpersonatedFromChildUser
        ? []
        : [
              {
                  label: 'Edit member',
                  callback: () => onEditMember(item)
              },
              {
                  label: 'Manage access to templates',
                  disabled: item.accessMode == AccessMode.FULL,
                  divider: true,
                  callback: () => {
                      item.accessMode != AccessMode.FULL
                          ? onManageMemberEntityAccess(item, EntityType.TEMPLATE)
                          : undefined;
                  },
                  class: item.accessMode == AccessMode.FULL ? 'text-danger-dark' : undefined
              },
              {
                  label: 'Manage access to libraries',
                  disabled: item.accessMode == AccessMode.FULL,
                  divider: true,
                  callback: () => {
                      item.accessMode != AccessMode.FULL
                          ? onManageMemberEntityAccess(item, EntityType.LIBRARY)
                          : undefined;
                  }
              },
              {
                  label: 'Manage access to stores',
                  disabled: item.accessMode == AccessMode.FULL,
                  divider: true,
                  callback: () => {
                      item.accessMode != AccessMode.FULL
                          ? onManageMemberEntityAccess(item, EntityType.STORE)
                          : undefined;
                  }
              },
              {
                  label: 'Manage access to products',
                  disabled: item.accessMode == AccessMode.FULL,
                  divider: true,
                  callback: () => {
                      item.accessMode != AccessMode.FULL
                          ? onManageMemberEntityAccess(item, EntityType.PRODUCT)
                          : undefined;
                  }
              },
              {
                  label: 'Manage access to option sets',
                  disabled: item.accessMode == AccessMode.FULL,
                  divider: true,
                  callback: () => {
                      item.accessMode != AccessMode.FULL
                          ? onManageMemberEntityAccess(item, EntityType.OPTION_SET)
                          : undefined;
                  }
              },
              {
                  label: 'Delete member',
                  divider: true,
                  callback: () => onDeleteMember(item),
                  class: 'text-danger-dark'
              }
          ].filter((i) => !i.disabled);
};

const userItems = computed(() => {
    return props.members.map((i) => ({
        initials: i.user.Name.toUpperCase().slice(0, 2),
        date: formatDate(i.user.CreatedDate!, DateFormat.MMMddYYYYTime),
        ...i
    }));
});

const onEditMember = (user: TeamMember) => {
    emit('edit-member', user);
};

const onManageMemberEntityAccess = (user: TeamMember, entityType: EntityType) => {
    emit('manage-member-entity-access', { member: user, entityType });
};

const onDeleteMember = (user: TeamMember) => {
    emit('delete-member', user);
};
</script>
<style lang="scss" scoped>
.disabled-element {
    background-color: #e9ecef;
    color: #6c757d;
}

.badge2 {
    padding: 2px;
    align-items: center;
    border-radius: var(--Rectangle, 4px);
    background: var(--Gray-scale-gray-lightest, #f7f7f7);
}

:global(.text-danger-dark) {
    color: var(--Status-Danger-danger-dark, #602) !important;
}

.table-header {
    color: #747474;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
}

.user-subtitle {
    color: #8c8c8c;
    font-size: 12px;
}

.team-list-wrapper {
    tbody {
        tr {
            &:hover {
                background-color: get-color('gray-100');

                .row-actions .btn {
                    visibility: initial;
                }
            }

            .row-actions {
                text-align: right;
                width: 1%;
                white-space: nowrap;

                .btn {
                    visibility: hidden;
                }

                .default-selected-visible {
                    visibility: initial !important;
                }
            }
        }
    }

    .row-category-container {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;

        .c-button-wrapper {
            visibility: hidden;
        }

        &:hover .c-button-wrapper {
            visibility: initial;
        }

        .category-preview {
            width: 50%;
            flex-grow: 1;
        }
    }
}
</style>
